const MB = 1024 * 1024;
const KB = 1024;
const IMAGE_FILE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"];

export const getFileSize = (bytes) => {
  if (!bytes) return `${0} bytes`;
  if (bytes > MB) {
    return `${(bytes / MB).toFixed(1)} MB`;
  } else if (bytes > KB) {
    return `${(bytes / KB).toFixed(1)} KB`;
  } else {
    return `${bytes} bytes`;
  }
};

export const isFileSizeGreaterThan = (bytes, limitMB) => {
  if (!bytes) return false;
  const limitBytes = limitMB * 1024 * 1024;
  return bytes > limitBytes;
};

export const isImage = (format) => {
  return IMAGE_FILE_EXTENSIONS.includes(format);
};

export const downloadFile = (file) => {
  const link = document.createElement("a");
  link.href = file?.fileURL;
  link.download = file.filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const DB_NAME = "BlobCache";
const STORE_NAME = "files";

const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "url" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

export const getCachedBlob = async (url) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readonly");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(url);
    request.onsuccess = () => resolve(request.result?.blob || null);
    request.onerror = () => reject(request.error);
  });
};

export const cacheBlob = async (url, blob) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.put({ url, blob, timestamp: Date.now() });
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

export const fetchWithProgress = async (url, onProgress) => {
  const urlWithoutSAS = url.split("?")[0];
  // Check cache first
  const cached = await getCachedBlob(urlWithoutSAS);
  if (cached) {
    console.log("read from cached");
    onProgress({ loaded: cached.size, total: cached.size });
    return new Response(cached);
  }

  console.log("fetch from cloud");
  // Fetch from cloud
  const response = await fetch(url);
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

  const contentLength = response.headers.get("Content-Length");
  if (!contentLength) throw new Error("Content-Length response header unavailable");

  const total = parseInt(contentLength, 10);
  let loaded = 0;

  const reader = response.body.getReader();
  const chunks = [];

  const stream = new ReadableStream({
    start(controller) {
      function push() {
        reader
          .read()
          .then(({ done, value }) => {
            if (done) {
              controller.close();
              // Cache the blob after download completes
              const blob = new Blob(chunks);
              cacheBlob(urlWithoutSAS, blob);
              return;
            }
            chunks.push(value);
            loaded += value.byteLength;
            onProgress({ loaded, total });
            controller.enqueue(value);
            push();
          })
          .catch((error) => {
            controller.error(error);
          });
      }
      push();
    },
  });

  return new Response(stream);
};
